/* ==========================================================================
   <fe-logo-slider> — the partner-logo strip on each hero panel.

   One row of logos, stepping a single card at a time. It never pauses: not on
   hover, not on focus.

   Stepping by the card rather than by the page is what lets one strip serve
   every width. How many cards the window holds is the stylesheet's business —
   four on a desktop, two on a phone — and the loop is as long as there are
   logos whichever it is, so no width can land on a half-empty page and there
   is no breakpoint written down here. The script reads --fe-logo-cols back off
   the element for the one thing it does need to know: whether there are more
   logos than fit, and so whether to travel at all.

   Round is the word that costs something. Past the last card the strip would
   have to travel all the way back to the first, which reads as a rewind rather
   than a loop — so the section prints copies of the first four at the end, and
   this glides into those. On the following tick, before moving on, it steps
   back onto the real ones with the transition switched off: the same logos are
   on screen either way, so the step is invisible, and the glide that follows
   sets out from the real ones.

   That step is taken on the tick rather than when the glide reports it has
   landed. A transition that never runs never reports landing — a panel the
   browser has decided not to paint, a device that skips it, a reader who has
   asked for reduced motion — and the strip would then walk off the end for
   ever. The interval is the thing that is actually reliable, so it drives
   both.

   Nothing here is hidden. The cards sit in a row and the track is translated,
   so every logo stays in the accessibility tree and in the tab order wherever
   the strip has got to. The copies at the end are aria-hidden, being copies.

   Reduced motion is handled in the stylesheet, and by cutting the glide rather
   than the loop: the logos arrive instead of travelling. Stopping would leave
   the strip frozen on the first few — and on Android that preference is
   reported for battery saver and for "remove animations", so it is not only
   readers who have asked for it who would land there.
   ========================================================================== */

if (!customElements.get('fe-logo-slider')) {
  customElements.define(
    'fe-logo-slider',
    class FeLogoSlider extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;

        this.track = this.querySelector('[data-slides]');
        this.slides = [...this.querySelectorAll('[data-slide]')];
        /* the whole beat — the slide and the rest after it — and the rest on
           its own, which is all the first move has to wait for */
        this.delay = Number(this.dataset.interval) || 0;
        this.pause = Number(this.dataset.pause) || 0;

        if (!this.track || this.slides.length < 2 || !this.delay) return;

        this.index = 0;
        this.width = 0;

        /* The column count changes with width, and so does whether there is
           anything to travel — asked of the element's own box rather than of
           the window, so a panel that is narrow for its own reasons is read
           correctly too. A ResizeObserver rather than a resize listener: on
           Android the browser bar collapsing on scroll fires resize without
           the strip having changed width at all. */
        this.ro = new ResizeObserver(([entry]) => {
          const w = Math.round(entry.contentRect.width);
          if (w === this.width) return;
          this.width = w;
          this.evaluate();
        });
        this.ro.observe(this);

        this.evaluate();
      }

      disconnectedCallback() {
        this.stop();
        this.ro?.disconnect();
      }

      /* how many cards the window holds, per the stylesheet. Four is the
         desktop count and the safe guess: erring high means a strip that has
         nowhere to go stays still, rather than one that travels its logos off
         the side. */
      get columns() {
        return Number(getComputedStyle(this).getPropertyValue('--fe-logo-cols')) || 4;
      }

      evaluate = () => {
        if (this.slides.length > this.columns) {
          /* the copies at the end are drawn only while there is a loop for
             them to hand over; the stylesheet keys that off this */
          this.setAttribute('data-loop', '');
          if (!this.timer) {
            /* The first move waits only the rest, not the rest and a slide.
               What a viewer should see at the start is the pause between two
               slides, and waiting a whole beat would be twice that. */
            this.timer = setTimeout(() => {
              this.next();
              this.timer = setInterval(this.next, this.delay);
            }, this.pause);
          }
          return;
        }

        /* every logo already fits: no travel, and none left off the side */
        this.stop();
        this.removeAttribute('data-loop');
        this.index = 0;
        this.track.style.setProperty('--fe-logo-i', 0);
      };

      stop() {
        /* the spec keeps one list of timers, so this clears either kind */
        clearTimeout(this.timer);
        clearInterval(this.timer);
        this.timer = 0;
      }

      next = () => {
        /* Standing on the copies: step onto the real cards they copy before
           setting off, so the glide below starts from the right place. */
        if (this.index >= this.slides.length) {
          this.track.setAttribute('data-snap', '');
          this.index = 0;
          this.track.style.setProperty('--fe-logo-i', 0);
          /* reading a layout property forces the style recalc that makes the
             step take effect on its own, rather than being folded into the
             glide that follows — which would be the rewind, drawn out */
          void this.track.offsetWidth;
          this.track.removeAttribute('data-snap');
        }

        /* One card, or the whole window at once. A card at a time reads as a
           row of logos filing past; a window at a time as one group leaving
           and the next arriving. Which of those is wanted is a matter of
           taste rather than of correctness, so the section decides.

           Never past the end, even where a window does not divide the logos
           evenly: landing exactly on it is what lets the snap above go
           unseen, because the window is then showing the copies of the very
           logos that position 0 shows. The cost is that the last move of a
           round can be a short one, which reads as a slide like any other. */
        const stride = this.dataset.step === 'window' ? this.columns : 1;
        this.index = Math.min(this.index + stride, this.slides.length);
        this.track.style.setProperty('--fe-logo-i', this.index);
      };
    }
  );
}
